import { useState } from "react";
import { toast } from "sonner";
import { Bot, Check, X } from "lucide-react";

const describe = (c, mix) => {
  if (c.op === "set_track") return `${c.track} ${c.field}: ${JSON.stringify(mix.tracks[c.track]?.[c.field] ?? 0)} → ${JSON.stringify(c.value)}`;
  if (c.op === "set_vocal_gain") return `vocal mix gain: ${mix.vocal.gain_db} dB → ${c.value} dB`;
  if (c.op === "set_master") return `master ${c.field}: ${JSON.stringify(mix.master[c.field])} → ${JSON.stringify(c.value)}`;
  if (c.op === "regenerate_stem") return `regenerate ${c.track} stem (new job, new seed)`;
  if (c.op === "set_setting") return `arrangement ${c.field} → ${JSON.stringify(c.value)} (new arrangement job)`;
  return c.op;
};

export default function Assistant({ pid, api, mix, arr, change, runJob }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [prop, setProp] = useState(null);
  const ask = async () => {
    if (msg.trim().length < 2) return;
    setBusy(true); setProp(null);
    try { setProp(await api("POST", `/projects/${pid}/assistant`, { message: msg, state: { mix, settings: arr.settings } })); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const apply = async () => {
    const mixOps = prop.commands.filter((c) => ["set_track", "set_vocal_gain", "set_master"].includes(c.op));
    if (mixOps.length) change((m) => mixOps.forEach((c) => {
      if (c.op === "set_track") m.tracks[c.track] = { ...(m.tracks[c.track] || {}), [c.field]: c.value };
      if (c.op === "set_vocal_gain") m.vocal.gain_db = c.value;
      if (c.op === "set_master") m.master[c.field] = c.value;
    }));
    for (const c of prop.commands.filter((x) => x.op === "regenerate_stem"))
      await runJob("regenerate_stem", { params: { base_arrangement_id: arr.id, stems: [c.track], settings: { seeds: { [c.track]: Math.floor(Math.random() * 1e6) } } } });
    const sets = prop.commands.filter((c) => c.op === "set_setting");
    if (sets.length) await runJob("arrange", { params: { settings: { ...arr.settings, ...Object.fromEntries(sets.map((c) => [c.field, c.value])) } } });
    toast.success("Applied the approved changes"); setProp(null); setMsg("");
  };
  return (
    <div className="card p-4 space-y-3" data-testid="assistant-panel">
      <div className="flex items-center gap-2 font-semibold text-sm"><Bot size={16} className="text-[var(--cyan)]" />Production assistant <span className="badge b-info">preview before apply</span></div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input className="input" placeholder="e.g. make the drums quieter, pan chords left and try a new bassline" value={msg} maxLength={600} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} data-testid="assistant-input" />
        <button className="btn btn-ghost" onClick={ask} disabled={busy} data-testid="assistant-submit">{busy ? "Thinking…" : "Propose changes"}</button>
      </div>
      {prop && (
        <div className="card-2 p-3 space-y-2 text-sm" data-testid="assistant-proposal">
          {prop.summary && <p>{prop.summary}</p>}
          {prop.commands.length === 0 ? <p className="muted">No applicable changes proposed.</p> :
            <ul className="space-y-1 font-mono text-xs">{prop.commands.map((c, i) => <li key={i} data-testid={`assistant-diff-${i}`}>• {describe(c, mix)}</li>)}</ul>}
          {prop.refused.length > 0 && <div className="text-xs text-[var(--orange)]">Not done: {prop.refused.join(" · ")}</div>}
          <div className="text-xs muted">Affected: {[...new Set(prop.commands.map((c) => c.track || c.field || "mix"))].join(", ") || "none"} · estimated charge {prop.estimated_credits} credits · {prop.model}</div>
          <div className="flex gap-2"><button className="btn btn-primary btn-sm" onClick={apply} disabled={!prop.commands.length} data-testid="assistant-apply"><Check size={14} />Apply</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setProp(null)} data-testid="assistant-discard"><X size={14} />Discard</button></div>
        </div>)}
    </div>
  );
}
